import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ItemSummaryCard from '../components/ItemSummary';
import { CITIES, CATEGORIES } from '../config/datamaps';
import { API_BASE_URL } from '../urldata';
import SearchBox from '../components/input/SearchBox';
import AppLinks from '../components/display/AppLinks';
import SEO from '../components/seo/SEO';
import Breadcrumbs from '../components/navigation/Breadcrumbs';

function ItemList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedSort, setSelectedSort] = useState('price:asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHits, setTotalHits] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  // Parse query params on load/change
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const q = queryParams.get('q') || '';
    const category = queryParams.get('category') || '';
    const city = queryParams.get('city') || '';
    const sort = queryParams.get('sort') || 'price:asc';
    const page = parseInt(queryParams.get('page')) || 1;

    setSearchQuery(q);
    setSelectedCategory(category);
    setSelectedCity(city);
    setSelectedSort(sort);
    setCurrentPage(page);

    // Only search if there's q, category, city OR if there's a specific sort (not default price:asc)
    // Actually, it's better to allow search if ANY filter/sort is present, 
    // or even just default to showing items if we are on the itemlist page.
    if (q || category || city || sort !== 'price:asc' || page > 1) {
      performSearch(q, category, city, sort, page);
    } else if (location.search) {
      // If there are ANY query params, perform search
      performSearch(q, category, city, sort, page);
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [location.search]);

  const performSearch = async (q, category, city, sort, page = 1) => {
    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      let params = `?q=${encodeURIComponent(q)}`;
      if (category) params += `&category=${encodeURIComponent(category)}`;
      if (city) params += `&city=${encodeURIComponent(city)}`;
      if (sort) params += `&sort=${encodeURIComponent(sort)}`;
      if (page) params += `&page=${page}`;

      const url = `${API_BASE_URL}/tapi/search${params}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Handle direct array response or data.results/data.data wrapper
      const results = Array.isArray(data) ? data : (data.results || data.data || []);
      setSearchResults(results);

      // Update pagination info
      if (data.total_pages) setTotalPages(data.total_pages);
      if (data.current_page) setCurrentPage(data.current_page);
      if (data.total_hits) setTotalHits(data.total_hits);
    } catch (err) {
      console.error("Failed to fetch search results:", err);
      setError(err.message || "Failed to fetch search results. Please try again.");
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFilters = (newFilters = {}) => {
    const params = new URLSearchParams(location.search);

    // Update params with new values, or use current state if not provided
    // Note: We use the *current state* as default for existing values, 
    // unless explicitly passed in newFilters.

    // Logic: If q is passed, use it. Else use searchQuery.
    const q = newFilters.q !== undefined ? newFilters.q : searchQuery;
    const category = newFilters.category !== undefined ? newFilters.category : selectedCategory;
    const city = newFilters.city !== undefined ? newFilters.city : selectedCity;
    const sort = newFilters.sort !== undefined ? newFilters.sort : selectedSort;
    const page = newFilters.page !== undefined ? newFilters.page : (newFilters.q || newFilters.category || newFilters.city ? 1 : currentPage);

    if (q) params.set('q', q); else params.delete('q');
    if (category) params.set('category', category); else params.delete('category');
    if (city) params.set('city', city); else params.delete('city');
    if (sort) params.set('sort', sort); else params.delete('sort');
    if (page && page > 1) params.set('page', page); else params.delete('page');

    navigate({ search: params.toString() });
  };

  const handleSearchBoxSubmit = () => {
    updateFilters({ q: searchQuery });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    navigate('/itemlist'); // Clear all filters? Or just query?
    // User probably expects clear button to clear the text. 
    // If they were searching, clearing text usually resets the query param?
    // If I just set state to '', it doesn't trigger search until submit.
    // But SearchBox has a dedicated 'onClear'.
    // Let's make it clear the query param immediately.
    updateFilters({ q: '' });
  };

  const categoryName = CATEGORIES[selectedCategory];
  const cityName = CITIES[selectedCity];
  const pageTitle = searchQuery
    ? `Search results for "${searchQuery}"`
    : (categoryName && cityName ? `${categoryName} in ${cityName}` : (categoryName || cityName || 'All Items'));

  const breadcrumbItems = [
    { label: 'All Items', link: '/items' }
  ];
  if (selectedCategory) breadcrumbItems.push({ label: categoryName, link: `/itemlist?category=${selectedCategory}` });
  if (selectedCity) breadcrumbItems.push({ label: cityName, link: `/itemlist?city=${selectedCity}` });
  if (searchQuery) breadcrumbItems.push({ label: `Search: ${searchQuery}` });

  return (
    <div className="container-fluid p-0">
      <SEO
        title={`${pageTitle} | ToyTrade.pk`}
        description={`Browse ${pageTitle} on ToyTrade.pk. Buy, sell or trade toys with parents in ${cityName || 'Pakistan'}.`}
        canonical={window.location.href}
      />

      <div className="px-4 pt-3">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <div className="  my-4">
        <SearchBox
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={handleSearchBoxSubmit}
          onClear={handleClearSearch}
          placeholder="Search item..."
        />
      </div>

      {/* Page Title based on category/city when no search query */}
      {(!location.search.includes('q=') || searchQuery === '') && (selectedCategory || selectedCity) && (
        <div className="text-center my-3">
          <h1 className="fs-2">
            {(() => {
              const categoryName = CATEGORIES[selectedCategory];
              const cityName = CITIES[selectedCity];
              if (categoryName && cityName) {
                return `${categoryName} in ${cityName}`;
              }
              return categoryName || cityName || 'All Items';
            })()}
          </h1>
        </div>
      )}

      {/* Show filters if results exist or filters are active */}
      {!isLoading && (hasSearched || selectedCategory || selectedCity) && (
        <div className=" mb-4">
          <div className="row text-start ps-4">
            <div className="col-lg-4 mb-2">
              <select className="form-select" value={selectedCity} onChange={(e) => updateFilters({ city: e.target.value })}>
                <option value="">All Cities</option>
                {Object.entries(CITIES).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div className="col-lg-4 mb-2">
              <select className="form-select" value={selectedCategory} onChange={(e) => updateFilters({ category: e.target.value })}>
                <option value="">All Categories</option>
                {Object.entries(CATEGORIES).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div className="col-lg-4 mb-2">
              <select className="form-select" value={selectedSort} onChange={(e) => updateFilters({ sort: e.target.value })}>
                <option value="price:asc">Price - low to high</option>
                <option value="price:desc">Price - high to low </option>
                <option value="created_at:desc">Newest</option>
                <option value="created_at:asc">Oldest</option>
                <option value="views:desc">Most Popular</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div id="itemList" className="  mt-4 ps-4">
        {isLoading && <div className="text-center"><p>Loading search results...</p></div>}
        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        {!isLoading && !error && hasSearched && searchResults.length === 0 && (
          <div className="text-center"><p>No items found.</p></div>
        )}

        {!isLoading && !error && searchResults.length > 0 && (
          <>
            <div className="row">
              {searchResults.map(item => (
                <div className="col-lg-3 col-md-4 col-sm-6 mb-4" key={item.id}>
                  <ItemSummaryCard item={item} />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <nav aria-label="Page navigation" className="my-4">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => updateFilters({ page: currentPage - 1 })} aria-label="Previous page">Previous</button>
                  </li>
                  {(() => {
                    const items = [];
                    const maxVisible = 6; // Number of page buttons to show before using ellipsis

                    if (totalPages <= 10) {
                      for (let i = 1; i <= totalPages; i++) items.push(i);
                    } else {
                      // Always show first page
                      items.push(1);

                      const start = Math.max(2, currentPage - maxVisible);
                      const end = Math.min(totalPages - 1, currentPage + maxVisible);

                      if (start > maxVisible) items.push('...');

                      for (let i = start; i <= end; i++) {
                        if (!items.includes(i)) items.push(i);
                      }

                      if (end < totalPages - 1) items.push('...');

                      // Always show last page
                      if (!items.includes(totalPages)) items.push(totalPages);
                    }

                    return items.map((page, index) => (
                      <li key={index} className={`page-item ${currentPage === page ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => page !== '...' && updateFilters({ page: page })}
                          disabled={page === '...'}
                          aria-label={page === '...' ? undefined : `Go to page ${page}`}
                          aria-current={currentPage === page ? 'page' : undefined}
                        >
                          {page}
                        </button>
                      </li>
                    ));
                  })()}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => updateFilters({ page: currentPage + 1 })} aria-label="Next page">Next</button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>

      {searchResults.length > 5 && (
        <div id="moreItems" className=" text-center my-4">

        </div>
      )}
      <div className="container pb-5">
      </div>
    </div>
  );
}

export default ItemList;
